// What the Studio shows for each template family: how its built-in templates look as cards
// in the template picker, and what the content can become (its outputs, see
// output-panel.mjs). Every Studio document can become a PDF and a lecture screen; a family
// adds the outputs only it can make (the Saju book needs the product's chart data). A new
// family gets its own picker tab by being a preset in document.mjs; an entry here improves
// its cards and adds its own outputs.
import { SAJU_CHARTS, SAJU_LOCKED_NOTE, SAJU_LOCKED_ROLES, SAJU_PRODUCTS, SAJU_READ_ONLY_SECTIONS } from './templates.mjs';
import { sajuBookOutput } from './saju-book-output.mjs';
import { printOutput } from './print-output.mjs';
import { lectureOutput } from './lecture-output.mjs';

const CHART_NAMES = { palja: '명식', relations: '합충', daeun: '대운', 'seun-century': '100년 세운', seun: '세운', wolun: '월운' };

/** '재물운 · 개인화 편집 샘플' → { title: '재물운', description: '개인화 편집 샘플' } */
function labelCard(example) {
  const [title, ...rest] = example.label.split(' · ');
  return { title, description: rest.join(' · '), meta: '' };
}

const FAMILIES = Object.freeze({
  'saju-editorial': Object.freeze({
    cardOf(example) {
      const product = SAJU_PRODUCTS.find(entry => entry.id === example.productId);
      if (!product) return labelCard(example);
      return { title: product.title, description: product.question,
        meta: `차트 · ${product.charts.map(kind => CHART_NAMES[kind] ?? SAJU_CHARTS[kind]?.heading ?? kind).join(' · ')}` };
    },
    outputs: Object.freeze([sajuBookOutput]),
    // Sections whose text the book does not take from the document: their pages are read only.
    readOnlySections: SAJU_READ_ONLY_SECTIONS,
    // Text on editable pages the book does not take from the document: locked on the canvas.
    lockedRoles: SAJU_LOCKED_ROLES,
    lockedNote: SAJU_LOCKED_NOTE,
    // A Saju product is a book: one Studio page per template section.
    pagePerSection: true,
  }),
});

/** What any Studio document can become, after the family's own outputs. */
const COMMON_OUTPUTS = Object.freeze([printOutput, lectureOutput]);

/** A built-in template as a picker card: { title, description, meta }. */
export function templateCard(familyId, example) {
  const card = (FAMILIES[familyId]?.cardOf ?? labelCard)(example);
  if (example.editor) {
    card.description = example.description;
    card.meta = example.editor === 'penpot' ? 'Penpot · 디자인 레이어 편집' : 'BlockSuite · 글 문서 편집';
  }
  return card;
}

/** Everything the open content can become, [{ output, subject }], the family's own first. */
export function outputsFor(content) {
  const outputs = [...Object.values(FAMILIES).flatMap(family => family.outputs ?? []), ...COMMON_OUTPUTS];
  return outputs.flatMap(output => {
    const subject = output.subjectOf(content);
    return subject ? [{ output, subject }] : [];
  });
}

/**
 * Why a Studio page cannot be edited — every template section it holds is computed by the
 * output rather than written in the template — or null.
 */
export function readOnlyReason(section) {
  const readOnly = FAMILIES[section?.origin?.templateId]?.readOnlySections;
  if (!readOnly) return null;
  const own = [...new Set(Object.values(section.origin.elements).map(role => role.split('.')[0]))].filter(name => name !== 'document');
  return own.length && own.every(name => readOnly[name]) ? readOnly[own[0]] : null;
}

/** Whether a template text role is kept locked on the canvas (the family does not read it back). */
export function lockedRole(familyId, role) {
  return Boolean(FAMILIES[familyId]?.lockedRoles?.includes(role));
}

/** What a Studio page says about its locked text, or null when it has none. */
export function lockedNote(section) {
  const family = FAMILIES[section?.origin?.templateId];
  return family?.lockedRoles && Object.values(section.origin.elements).some(role => family.lockedRoles.includes(role)) ? family.lockedNote : null;
}

/** Whether a template of this family opens as one Studio page per section (else one page). */
export function pagePerSection(familyId) {
  return Boolean(FAMILIES[familyId]?.pagePerSection);
}

/**
 * A built-in template's page counts, as it is edited: one edited on its book's pages (a Saju product) is counted by
 * its published book — its pages and the page templates they are laid out from; any other by its Studio pages.
 */
export async function templatePageCounts(familyId, example, document) {
  if (example.productId) {
    const subject = { id: example.productId };
    const book = await sajuBookOutput.pageCount(subject);
    if (book) return { book, templates: await sajuBookOutput.pageTemplateCount(subject) };
  }
  return { studio: [2, 3].includes(document.schemaVersion) || pagePerSection(familyId) && document.sections.length > 1 ? document.sections.length : 1 };
}
